import { AxiosError } from "axios";
import config from "config";
import * as Sentry from "@sentry/react";

import { getTenantFromHostname } from "shared/utils";

// A presigned S3 URL carries its signature and credential in the query string.
// The start anchor also matches a bare query string, as in the url.query attribute.
const PRESIGNED_URL = /(?:^|[?&])X-Amz-Signature=/i;

const isPresignedUrl = (value: unknown): value is string =>
  typeof value === "string" && PRESIGNED_URL.test(value);

// The path holds the record ID and the user's file name, so only the origin is kept.
const redactPresignedUrl = (url: string): string => {
  try {
    return `${new URL(url).origin}/[presigned]`;
  } catch {
    return "[presigned]";
  }
};

export const initializeSentry = (): void => {
  if (!shouldInitializeSentry()) {
    console.log("Skipping Sentry init!");

    return;
  }

  const { instance, sentry } = config.get();
  const tenant = getTenantFromHostname();

  Sentry.init({
    dsn: sentry!.dsn, // sentry is defined at this point - see shouldInitializeSentry
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Replay records image loads, such as presigned attachment GETs, as
        // spans that skip beforeSendSpan.
        beforeAddRecordingEvent(event) {
          if (
            event.data.tag !== "performanceSpan" ||
            !isPresignedUrl(event.data.payload.description)
          ) {
            return event;
          }

          return {
            ...event,
            data: {
              ...event.data,
              payload: {
                ...event.data.payload,
                description: redactPresignedUrl(event.data.payload.description),
              },
            },
          };
        },
      }),
    ],
    tracesSampler: ({ name, attributes }) => {
      const isApiError =
        name.includes("API Error") ||
        attributes.error === true ||
        (typeof attributes.status_code === "number" &&
          attributes.status_code >= 400);

      return isApiError ? 1.0 : (sentry?.tracesSampleRate ?? 0.1);
    },
    tracePropagationTargets: [/^https:\/\/.*\.viaduct\.ai(\/.*)?$/], // Any subdomain ending with .viaduct.ai, with optional path
    replaysSessionSampleRate: sentry?.replaysSessionSampleRate ?? 0.1,
    replaysOnErrorSampleRate: sentry?.replaysOnErrorSampleRate ?? 1.0,
    environment: instance,
    // The LaunchDarkly SDK fetches flags in the background and rejects
    // unhandled on transient network failures. No app code can catch that
    // rejection, and it is not actionable.
    ignoreErrors: [/^LaunchDarklyFlagFetchError: network error/],
    // Fetch breadcrumbs keep the full URL, and replay copies them into its
    // network tab.
    beforeBreadcrumb(breadcrumb) {
      const url = breadcrumb.data?.url;
      if (!isPresignedUrl(url)) return breadcrumb;

      return {
        ...breadcrumb,
        data: { ...breadcrumb.data, url: redactPresignedUrl(url) },
      };
    },
    // Sentry's own query filter only masks keys like "token". Signed URLs sit in
    // url.full, url.query and the LCP span's browser.web_vital.lcp.url.
    beforeSendSpan(span) {
      if (!Object.values(span.attributes).some(isPresignedUrl)) return span;

      return {
        ...span,
        attributes: Object.fromEntries(
          Object.entries(span.attributes).map(([key, value]) => [
            key,
            isPresignedUrl(value) ? redactPresignedUrl(value) : value,
          ])
        ),
      };
    },
    beforeSend(event) {
      event.tags = {
        ...event.tags,
        tenant,
      };

      return event;
    },
  });
};

export const getSentryReactConfig = () => {
  const { sentry } = config.get();
  if (!sentry?.dsn) {
    return {};
  }

  return {
    // Callback called when an error is thrown and not caught by an ErrorBoundary.
    onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
      console.warn("Uncaught error", error, errorInfo.componentStack);
    }),
    // Callback called when React catches an error in an ErrorBoundary.
    onCaughtError: Sentry.reactErrorHandler(),
    // Callback called when React automatically recovers from errors.
    onRecoverableError: Sentry.reactErrorHandler(),
  };
};

const localhostHostnames = ["localhost", "[::1]", "viaduct.cloud.viaduct.ai"];
const isLocalhost =
  localhostHostnames.includes(window.location.hostname) ||
  // Matches IPv4 localhost addresses in the 127.x.x.x range (e.g., 127.0.0.1, 127.1.2.3)
  /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/.test(
    window.location.hostname
  );

const shouldInitializeSentry = (): boolean => {
  const { sentry } = config.get();

  if (isLocalhost) {
    return false;
  }

  return !!sentry?.dsn;
};

/**
 * Extracts a normalized URL pattern by replacing dynamic segments (IDs)
 * with `:id` placeholders, so Sentry can group errors by endpoint rather
 * than by specific resource.
 *
 * e.g. "/api/v1/vehicles/12345" → "/api/v1/vehicles/:id"
 *      "/api/v1/failureModes/b8fc0fbb-5b73-48d8-a682-49edc7be1372" → "/api/v1/failureModes/:id"
 */
const getUrlPattern = (url: string): string => {
  try {
    const pathname = url.startsWith("http") ? new URL(url).pathname : url;

    return pathname
      .split("/")
      .map((segment) => {
        if (!segment) return segment;

        // UUIDs
        if (
          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
            segment
          )
        ) {
          return ":id";
        }

        // Numeric IDs
        if (/^\d+$/.test(segment)) {
          return ":id";
        }

        return segment;
      })
      .join("/");
  } catch {
    return "unknown";
  }
};

const MAX_RESPONSE_DATA_LENGTH = 4096;

// Sentry's normalizeDepth cuts nested objects to "[Object]", but keeps strings.
const stringifyResponseData = (data: unknown): string | undefined => {
  if (data === undefined) return undefined;

  // A throw here would replace the Axios error the caller receives.
  try {
    const text = typeof data === "string" ? data : JSON.stringify(data);

    return text.slice(0, MAX_RESPONSE_DATA_LENGTH);
  } catch {
    return undefined;
  }
};

export const trackAxiosErrorWithSentry = (
  error: AxiosError, // AxiosError type
  clientType: string
) => {
  if (!shouldInitializeSentry() || !error.response) {
    return;
  }

  const statusCode = error.response.status;

  // 401s (expired sessions, token-refresh races — the app redirects to
  // login), 404s and 409s are almost always an expected outcome handled by
  // the caller, not a bug — capture them as warnings so alert rules
  // filtering on level:error don't notify, but they stay visible.
  const level: Sentry.SeverityLevel = [401, 404, 409].includes(statusCode)
    ? "warning"
    : "error";

  const url = error.config?.url || "unknown";
  const method = error.config?.method;
  const urlPattern = getUrlPattern(url);
  const requestID = error.response.headers["x-request-id"];

  Sentry.startSpan(
    {
      name: `API Error: ${error.config?.method?.toUpperCase()} ${url}`,
      op: "http.client.error",
      attributes: {
        error: true,
        status_code: statusCode,
        api_url: url,
        method,
        client_type: clientType,
      },
      forceTransaction: true,
    },
    () => {
      Sentry.captureException(error, {
        level,
        fingerprint: [
          "axios-error",
          String(statusCode),
          method ?? "unknown",
          urlPattern,
        ],
        tags: {
          component: clientType,
          url,
          status: statusCode,
          method,
          ...(typeof requestID === "string" && { requestID }),
        },
        contexts: {
          response: {
            status: statusCode,
            statusText: error.response?.statusText,
            data: stringifyResponseData(error.response?.data),
          },
          request: {
            url,
            method,
          },
        },
      });
    }
  );
};
